import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  TableBody,
  TextField,
  Typography,
} from "@mui/material";
import { IconButton, Tooltip } from "@mui/material";
import { DeleteOutline as DeleteIcon } from "@mui/icons-material";
import { CeldaKit, CeldaPrincipal, Columna, FilaKit, TablaKit } from "../kit/TablaKit";
import EstadoChip from "../kit/EstadoChip";
import { usePostulaciones, useResolverPostulacion, useRetirarPostulacion } from "../../queries/usePostulaciones";
import { useAuthStore } from "../../stores/authStore";
import { AccionPostulacion, Postulacion } from "../../types/postulacion";
import { formatFecha } from "../../utils/fecha";
import { iconoDePostulacion, tonoDePostulacion } from "../../utils/estados";
import { mensajeDeError } from "../../utils/errores";
import { Accesores, ordenar, useOrden } from "../../utils/orden";

const COLUMNAS: (string | Columna)[] = [
  { label: "Estudiante", sortKey: "estudiante" },
  { label: "Mensaje", sortKey: "mensaje" },
  { label: "Postuló", sortKey: "fecha" },
  { label: "Estado", sortKey: "estado" },
  { label: "Acciones", align: "right" as const },
];

const ACC_POST: Accesores<Postulacion> = {
  estudiante: (p) => p.estudiante_nombre,
  mensaje: (p) => p.mensaje,
  fecha: (p) => p.fecha_creacion,
  estado: (p) => p.estado_display,
};

/**
 * Solicitudes recibidas por un tema, para su guía (o el coordinador). Aceptar a un
 * estudiante rechaza al resto y asigna el tema, así que se confirma antes.
 *
 * `admiteResolucion` viene del backend (`Tema.admite_resolucion`) en vez de mirar
 * `disponible`: la condición real incluye las propuestas en revisión, y duplicarla
 * aquí fue lo que dejó sin botones a las propuestas de estudiante.
 */
export default function PostulacionesTema({ temaId, admiteResolucion }: { temaId: number; admiteResolucion: boolean }) {
  const { user } = useAuthStore();
  const { data, isLoading, isError } = usePostulaciones(temaId, true);
  const resolver = useResolverPostulacion(temaId);
  const eliminar = useRetirarPostulacion(temaId);
  const [confirmar, setConfirmar] = useState<{ postulacion: Postulacion; accion: AccionPostulacion } | null>(null);
  // Comentario del rechazo: viaja al correo del estudiante. Es opcional, pero un
  // "no" sin explicación no le dice qué hacer distinto la próxima vez.
  const [motivo, setMotivo] = useState("");
  const { orden, onSort } = useOrden();

  const postulaciones = data ?? [];

  const abrir = (postulacion: Postulacion, accion: AccionPostulacion) => {
    setMotivo("");
    setConfirmar({ postulacion, accion });
  };

  const ejecutar = () => {
    if (!confirmar) return;
    resolver.mutate(
      { id: confirmar.postulacion.id, accion: confirmar.accion, motivo: confirmar.accion === "rechazar" ? motivo : "" },
      { onSettled: () => setConfirmar(null) },
    );
  };

  return (
    <Box sx={{ mt: 5 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Solicitudes
        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          ({postulaciones.length})
        </Typography>
      </Typography>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {isError && <Alert severity="error">No se pudieron cargar las solicitudes.</Alert>}
      {resolver.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {mensajeDeError(resolver.error, "No se pudo resolver la solicitud.")}
        </Alert>
      )}

      {!isLoading && !isError && postulaciones.length === 0 && (
        <Paper elevation={0} sx={{ p: 3, textAlign: "center", border: "1px dashed", borderColor: "divider", borderRadius: 2 }}>
          <Typography color="text.secondary" variant="body2">
            Todavía no hay estudiantes postulando a este tema.
          </Typography>
        </Paper>
      )}

      {postulaciones.length > 0 && (
        <TablaKit columnas={COLUMNAS} minWidth={720} orden={orden} onSort={onSort}>
          <TableBody>
            {ordenar(postulaciones, ACC_POST, orden).map((p) => (
              <FilaKit key={p.id}>
                <CeldaPrincipal>
                  {p.estudiante_nombre}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 400 }}>
                    {p.estudiante_email || "—"}
                  </Typography>
                </CeldaPrincipal>
                <CeldaKit maxWidth={320} atenuada={!p.mensaje}>
                  {p.mensaje || "—"}
                </CeldaKit>
                <CeldaKit noWrap>{formatFecha(p.fecha_creacion)}</CeldaKit>
                <CeldaKit>
                  <EstadoChip label={p.estado_display} tono={tonoDePostulacion(p.estado)} icono={iconoDePostulacion(p.estado)} denso />
                </CeldaKit>
                <CeldaKit align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                    {admiteResolucion && p.estado === "pendiente" && (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          disabled={resolver.isPending}
                          onClick={() => abrir(p, "rechazar")}
                        >
                          Rechazar
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={resolver.isPending}
                          onClick={() => abrir(p, "aceptar")}
                        >
                          Aceptar
                        </Button>
                      </>
                    )}
                    {/* El coordinador puede eliminar cualquier solicitud. */}
                    {user?.es_coordinador_global && (
                      <Tooltip title="Eliminar solicitud">
                        <IconButton size="small" color="error" disabled={eliminar.isPending} onClick={() => eliminar.mutate(p.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!(admiteResolucion && p.estado === "pendiente") && !user?.es_coordinador_global && (
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </Stack>
                </CeldaKit>
              </FilaKit>
            ))}
          </TableBody>
        </TablaKit>
      )}

      <Dialog open={confirmar !== null} onClose={() => setConfirmar(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmar?.accion === "aceptar" ? "Aceptar estudiante" : "Rechazar solicitud"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmar?.accion === "aceptar" ? (
              <>
                Vas a aceptar a <strong>{confirmar.postulacion.estudiante_nombre}</strong>. Esto rechaza automáticamente al
                resto de los postulantes y el tema deja de estar disponible.
              </>
            ) : (
              <>
                Vas a rechazar la solicitud de <strong>{confirmar?.postulacion.estudiante_nombre}</strong>. El tema sigue
                disponible para el resto.
              </>
            )}
          </DialogContentText>
          {confirmar?.accion === "rechazar" && (
            <TextField
              label="Motivo (opcional)"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Por ejemplo: el tema necesita un curso que aún no has tomado."
              helperText="Se lo mandamos en el correo, con tu nombre."
              fullWidth
              multiline
              minRows={3}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color={confirmar?.accion === "aceptar" ? "primary" : "error"}
            disabled={resolver.isPending}
            onClick={ejecutar}
          >
            {confirmar?.accion === "aceptar" ? "Aceptar" : "Rechazar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
